import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import type { IOrderResponse } from '@/interface/IOrderResponse';
import type { IMixParlayBetSlip, IUpdateTicket } from '@/interface/betSlip';
import { BetSlipState, BetSlipType, DisplaySection, OpenTicketStatus, PlaceOrderStatus, StatementDetailStatus } from '@/interface/enum';
import type { PlaceComboParlayOrderRequest, TicketInfo } from '@/interface/pollux';
import Api, { fireAndForget } from '@/libraries/api';
import { calculateStake, isStakeAtLeastMinBet, isStakeLessBalance, isStakeWithinMaxBet } from '@/libraries/betSlip/stake';
import { useParlayBetPage } from '@/libraries/composables/useBetPage';
import useState from '@/libraries/composables/useState';
import type { IEuroTicket } from '@/libraries/euroTicket';
import { getOpenTicketErrorMessage } from '@/libraries/euroTicket';
import { getDefaultStake, isPlaceHierarchyClosed } from '@/libraries/helpers/betSlipHelper';
import { generateComboParlayCombination, getComboTotalOdds, getTotalBetCount, getTotalOdds, getTotalPayout } from '@/libraries/helpers/comboParlayHelper';
import { platform } from '@/libraries/helpers/platformHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { isCorrectScoreMarket } from '@/libraries/oddsDisplay';
import { getSportsBsiUrl } from '@/libraries/url';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const MINIMUM_MP_TWO_SUB_BET_COUNT = 2;
const MINIMUM_MP_SUB_BET_COUNT = 3;

export const useParlayBetSlipStore = defineStore('ParlayBetSlip', () => {
    const [betSlip, setBetSlip] = useState<IMixParlayBetSlip>(getDefaultMixParlayBetSlip());
    const [isPlacingBet, setIsPlacingBet] = useState(false);
    const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);
    const [stake, setStake] = useState(0);
    const [openFrom, setOpenFrom] = useState(DisplaySection.ParlayMode);

    const minBet = computed(() => betSlip.value.min);
    const maxBet = computed(() => betSlip.value.max);

    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());
    const { isSupportMpTwoSubBets } = useFeatureToggleStore();
    const { isMixParlayMode } = storeToRefs(useRootStore());
    const { formatMoneyWithSymbolAndDecimal, setBalance } = useAccountStore();
    const { balance } = storeToRefs(useAccountStore());
    const { refreshBetCount } = useBetCountStore();
    const { setBetSlipState, setBetSlipType, showMiniBetSlip } = useBetSlipStore();

    const { setTopScreenToast, setShowAcceptAnyOddsBanner, setFailedPlaceBetShowAaoBanner } = useRootStore();

    const subBets = computed(() => Object.values(betSlip.value.tickets));
    const allOdds = computed(() => subBets.value.map(x => x.price));

    const comboOdds = computed(() => allOdds.value.reduce((result: Record<number, number[][]>, _, i) => {
        const comboSelection = i + 2;
        const combinations = generateComboParlayCombination(allOdds.value, comboSelection);
        result[comboSelection] = combinations;
        return result;
    }, {}));

    const comboTotalOdds = computed(() => getComboTotalOdds(comboOdds.value));
    const totalPayOutWithComboParlay = computed(() => formatMoneyWithSymbolAndDecimal(getTotalPayout(betSlip.value.selectedCombo, comboTotalOdds.value, stake.value)));
    const price = computed(() => Math.floor(betSlip.value.selectedCombo
        .reduce((acc, cur) => acc + comboTotalOdds.value[cur], 0) * 1000) / 1000);

    const ticketCount = computed(() => Object.keys(betSlip.value.tickets).length);
    const totalOdds = computed(() => getTotalOdds(allOdds.value));
    const totalOddsDisplay = computed(() => formatMinDecimalPlace(totalOdds.value, 2));
    const totalPayoutDisplay = computed(() => formatMoneyWithSymbolAndDecimal(totalOdds.value * stake.value));
    const totalBetCount = computed(() => getTotalBetCount(betSlip.value.selectedCombo, comboOdds.value));
    const totalStake = computed(() => totalBetCount.value * stake.value);

    const isTicketExist = (key: string) => Object.keys(betSlip.value.tickets).includes(key);

    const isValid2MpSubBets = computed(() => ticketCount.value === 2 && subBets.value.every(x => isSupportMpTwoSubBets(x.leagueId)));
    const isValidSubBetCount = computed(() => ticketCount.value >= MINIMUM_MP_SUB_BET_COUNT || isValid2MpSubBets.value);
    const isValidMpStake = computed(() => isStakeWithinMaxBet(stake.value, betSlip.value.max)
        && isStakeAtLeastMinBet(stake.value, betSlip.value.min)
        && isStakeLessBalance(totalStake.value, balance.value, price.value)
        && stake.value > 0);

    const countOfAddMore = computed(() => {
        if (isValidSubBetCount.value) {
            return 0;
        }

        const isNeedMoreMpTwoSubBet = ticketCount.value > 0
            && ticketCount.value < MINIMUM_MP_TWO_SUB_BET_COUNT
            && subBets.value.every(x => isSupportMpTwoSubBets(x.leagueId));
        const minMpSubBetCount = isNeedMoreMpTwoSubBet
            ? MINIMUM_MP_TWO_SUB_BET_COUNT
            : MINIMUM_MP_SUB_BET_COUNT;
        return minMpSubBetCount - ticketCount.value;
    });

    const getPlaceOrderMsgData = computed(() => (response: IOrderResponse) => {
        let subEvent = '';
        const key = Object.keys(betSlip.value.tickets)
            .find(k => betSlip.value.tickets[k].eventId === response.extraInfo);
        if (response.placeOrderStatus === PlaceOrderStatus.MPMaxAccumulatedStakeForSubEventExceed) {
            if (key) {
                const ticket = betSlip.value.tickets[key];
                const homeTeam = getTranslated(ticket.event.homeTeam?.teamName);
                const awayTeam = getTranslated(ticket.event.awayTeam?.teamName);
                subEvent = `${homeTeam} -VS- ${awayTeam}`;
            }
            return { subEvent };
        }

        return {};
    });

    const totalParlayStakeDisplay = computed(() => formatMoneyWithSymbolAndDecimal(totalStake.value));

    const comboParlaySelection = computed(() => {
        if (!isValidSubBetCount.value) {
            return i18n.t('bottom_function_bet_slip');
        }

        if (subBets.value.length === 3) {
            return i18n.t('betSlip_comboParlay_option_3');
        }

        return i18n.t('betSlip_comboParlay_option_n', { n: subBets.value.length });
    });

    const placeBet = async () => {
        if (!isValidMpStake.value) {
            adjustStake();
            return;
        }
        if (isPlacingBet.value) {
            return;
        }

        setIsPlacingBet(true);

        const bet = Object.keys(betSlip.value.tickets).map((key) => {
            const ticket = betSlip.value.tickets[key];
            return {
                eventId: ticket.eventId,
                marketType: ticket.marketType,
                oddsId: ticket.oddsId,
                option: ticket.option,
                leagueId: ticket.leagueId,
                point: ticket.point,
                liveScore: {
                    home: ticket.odds.eventResult.liveHomeScore,
                    away: ticket.odds.eventResult.liveAwayScore,
                },
            };
        });

        const isComboParlay = !(betSlip.value.selectedCombo.length === 1 && betSlip.value.selectedCombo.includes(bet.length));
        const { betPage } = useParlayBetPage(openFrom.value, isComboParlay);

        const request: PlaceComboParlayOrderRequest = {
            stake: stake.value,
            uid: betSlip.value.uid,
            mpBet: bet,
            selectedCombo: betSlip.value.selectedCombo,
            betPage: betPage.value,
        };

        try {
            const [response] = await Promise.all([Api.placeComboParlayBet(request), sleep(placeBetDelayTime.value)]);
            const { isPlaceBetSuccess, placeOrderStatus, orderStatus, message } = response;

            if (isPlaceBetSuccess) {
                setTopScreenToast({
                    status: 'placeBetSuccess',
                    messageKey: orderStatus === StatementDetailStatus.Running ? 'betSlip_placeBetConfirmed' : 'betSlip_placeBetBeingProcessed',
                });

                setTimeout(() => {
                    refreshBetCount(false);
                    platform.updateBalance();
                    window.eventEmitter.emit('refreshBetList');
                }, 1000);

                setShowAcceptAnyOddsBanner(true);

                setBetSlipState(BetSlipState.Closed);
                setBetSlipType(BetSlipType.None);
            } else if (placeOrderStatus === PlaceOrderStatus.RetryOpenTicket) {
                await openTicket(false);
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_generalFailure',
                });
            } else if (isPlaceHierarchyClosed(placeOrderStatus)) {
                window.location.href = getSportsBsiUrl();
            } else if (placeOrderStatus === PlaceOrderStatus.OrderProcessing) {
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_orderProcessing',
                });
            } else {
                setTopScreenToast({
                    status: 'error',
                    messageKey: message,
                    messageData: getPlaceOrderMsgData.value(response),
                });
                refreshBetCount(false);
                setFailedPlaceBetShowAaoBanner(true);
            }
        } catch {
            setFailedPlaceBetShowAaoBanner(true);
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_goToMyBetToCheckBetStatus',
            });
        } finally {
            setIsPlacingBet(false);
            setBetSlip(getDefaultMixParlayBetSlip());
            setStake(0);
        }
    };

    const updateTicket = ({
        key, maxBet, minBet, oddsData, messageKey, messageData,
    }: IUpdateTicket) => {
        const ticket = betSlip.value.tickets[key];
        if (!ticket) return;

        if (maxBet !== undefined) {
            ticket.maxBet = maxBet;
        }

        if (minBet !== undefined) {
            ticket.minBet = minBet;
        }

        if (oddsData) {
            if (ticket.odds) {
                ticket.odds.eventResult.liveHomeScore = oddsData.liveHomeScore;
                ticket.odds.eventResult.liveAwayScore = oddsData.liveAwayScore;
            }
            ticket.point = oddsData.point;
            ticket.price = oddsData.price;
            ticket.betCondition = oddsData.betCondition;
            ticket.priceStyle = oddsData.priceStyle;
            ticket.uid = oddsData.uid ?? ticket.uid;
        }

        if (messageKey) {
            window.clearTimeout(ticket.message.timeoutId);
            ticket.message.key = messageKey;
            ticket.message.data = messageData;
            ticket.message.timeoutId = window.setTimeout(() => {
                ticket.message.key = null;
                ticket.message.data = null;
                ticket.message.timeoutId = 0;
            }, 3000);
        }
    };

    const removeTicket = (key: string | number) => {
        const newTickets = { ...betSlip.value.tickets };
        delete newTickets[key];
        betSlip.value.tickets = newTickets;
        resetSelectedCombo();
    };

    async function removeBetSlip() {
        betSlip.value.tickets = {};
        setStake(0);
        fireAndForget(Api.clearTicket());
        setBetSlipState(BetSlipState.Closed);
        resetSelectedCombo();
        setBetSlipType(BetSlipType.None);
        setOpenFrom(DisplaySection.ParlayMode);
    }

    async function removeTicketOrBetSlip(key: string | number) {
        if (ticketCount.value === 1) {
            await removeBetSlip();
        } else {
            removeTicket(key);
        }
    }

    let cancelToken: CancelTokenSource;
    async function openTicket(isFirstTime: boolean) {
        if (isUpdatingTicket.value && cancelToken) {
            cancelToken.cancel();
            setIsUpdatingTicket(false);
        }

        if (!isValidSubBetCount.value) {
            return;
        }

        setIsUpdatingTicket(true);

        const requests: TicketInfo[] = Object.keys(betSlip.value.tickets).map((key) => {
            const ticket = betSlip.value.tickets[key];
            return {
                oddsId: ticket.oddsId,
                option: ticket.option,
                marketType: ticket.marketType,
                sportType: ticket.sportType,
                eventId: ticket.eventId,
                marketPageType: ticket.marketPageType,
                isLive: ticket.isLive,
            };
        });

        cancelToken = axios.CancelToken.source();

        try {
            const response = await Api.openMixParlayTicket(requests, { cancelToken: cancelToken.token });
            if (!response.oddsInfo) {
                if (response.errorCode === OpenTicketStatus.MaxOddsExceed) {
                    setTopScreenToast({
                        status: 'error',
                        messageKey: 'betSlip_errorMessage_overMaxOdds',
                    });
                }
                throw response;
            }

            if (isFirstTime) {
                const defaultStake = getDefaultStake(
                    response.minBet,
                    response.maxBet,
                    stake.value,
                );
                setStake(defaultStake);
            }

            Object.keys(betSlip.value.tickets).forEach((key) => {
                const ticket = betSlip.value.tickets[key];
                const ticketData = response.oddsInfo?.[key];
                const minDecimalPlace = isCorrectScoreMarket(ticket.marketType) ? 0 : 2;

                if (!ticketData) {
                    updateTicket({
                        key,
                        messageKey: 'betSlip_errorMessage_oddsClosed',
                    });
                    setTimeout(async () => {
                        await removeTicketOrBetSlip(key);
                    }, 3000);
                } else {
                    updateTicket({
                        key,
                        oddsData: ticketData,
                        messageKey: getOpenTicketErrorMessage(
                            ticketData.errorCode,
                            ticket,
                            ticketData,
                            ticket.priceStyle === ticketData.priceStyle,
                        ),
                        messageData: {
                            oldOdds: formatMinDecimalPlace(ticket.price.toString(), minDecimalPlace),
                            newOdds: formatMinDecimalPlace(ticketData.price.toString(), minDecimalPlace),
                            oldScore: `[${ticket.odds.eventResult.liveHomeScore}:${ticket.odds.eventResult.liveAwayScore}]`,
                            newScore: `[${ticketData.liveHomeScore}:${ticketData.liveAwayScore}]`,
                            oldPoint: ticket.point.toFixed(2),
                            newPoint: ticketData.point.toFixed(2),
                        },
                    });
                }
            });

            betSlip.value.min = response.minBet;
            betSlip.value.max = response.maxBet;
            betSlip.value.uid = response.mpUid;

            setBalance(response.balance);

            if (response.maxBet < response.minBet) {
                setTopScreenToast({
                    status: 'error',
                    messageKey: 'betSlip_errorMessage_overMaxOdds',
                });
            }

            setIsUpdatingTicket(false);
        } catch (e) {
            if (axios.isCancel(e)) {
                return;
            }
            setIsUpdatingTicket(false);
        }
    }

    const { betSlipType, isShowMediumBetSlip } = storeToRefs(useBetSlipStore());
    const addTicket = (ticket: IEuroTicket) => {
        betSlip.value.tickets = {
            ...betSlip.value.tickets,
            [`${ticket.oddsId}-${ticket.option}`]: ticket,
        };
        resetSelectedCombo();
        const state = isShowMediumBetSlip.value
            ? BetSlipState.Medium
            : BetSlipState.Mini;

        setBetSlipState(state);
        setBetSlipType(BetSlipType.Parlay);
    };

    function resetSelectedCombo() {
        betSlip.value.selectedCombo = [];
        if (ticketCount.value >= 2) {
            betSlip.value.selectedCombo = [ticketCount.value];
        }
    }

    function adjustStake() {
        const stakeInfo = calculateStake(
            betSlip.value.max,
            betSlip.value.min,
            balance.value,
            stake.value,
            price.value,
        );

        setStake(stakeInfo.stake);

        setTopScreenToast({
            status: 'error',
            messageKey: stakeInfo.message,
        });
    }

    async function refresh(isFirstTime: boolean) {
        await openTicket(isFirstTime);
    }

    watch(isMixParlayMode, async (newValue) => {
        if (newValue && ticketCount.value > 0) {
            if (betSlipType.value !== BetSlipType.Parlay) {
                setBetSlipType(BetSlipType.Parlay);
                showMiniBetSlip();
            }
            await openTicket(true);
        }
    });

    watch(subBets, () => {
        refresh(false);
    });

    return {
        betSlip,
        totalOdds,
        totalOddsDisplay,
        totalStake,
        openTicket,
        totalPayoutDisplay,
        isUpdatingTicket,
        isValid2MpSubBets,
        isValidSubBetCount,
        isTicketExist,
        ticketCount,
        countOfAddMore,
        isPlacingBet,
        stake,
        minBet,
        maxBet,
        setStake,
        setBetSlip,
        placeBet,
        addTicket,
        removeTicket,
        updateTicket,
        comboOdds,
        comboTotalOdds,
        totalBetCount,
        resetSelectedCombo,
        price,
        totalParlayStakeDisplay,
        comboParlaySelection,
        removeBetSlip,
        removeTicketOrBetSlip,
        refresh,
        subBets,
        totalPayOutWithComboParlay,
        setOpenFrom,
    };
});

function getDefaultMixParlayBetSlip(): IMixParlayBetSlip {
    return {
        min: 0,
        max: 0,
        uid: '',
        tickets: {},
        selectedCombo: [],
    };
}
